import jsPDF from 'jspdf';
import { formatCountry } from './formatters';

/**
 * Render a single invoice / credit-memo PDF and trigger a browser
 * download. Designed to take the same payload shape the InvoiceDraft
 * page builds, so the on-screen preview and the downloaded PDF stay in
 * lock-step.
 *
 * Payload shape:
 *   {
 *     invoice: { InvoiceNumber, IssueDate, DueDate, Status,
 *                CustomerName, BillingAddress, PoNumber, Notes,
 *                PeriodStart, PeriodEnd, ParentInvoiceNumber,
 *                PaymentTerms, contactNames, ... },
 *     periodLabel: 'Aug 16, 2026 – Aug 29, 2026',
 *     customer: { ... supplier record with Title, address fields,
 *                 SupplierCode, plantAssignments (per-plant coverage) },
 *     lineItems: [{ description, details, submitterName, quantity,
 *                   unitLabel, rate, amount, plantCode, plantTitle,
 *                   dateRangeStart, dateRangeEnd, weekStart, ... }],
 *     linesByPlant: [[plantCode, [li, ...]], ...] (optional). Adjustment
 *       lines belong to the '—' bucket and render at the very bottom
 *       under an "Adjustments" banner.
 *     appendix: {                      // optional Hours-Breakdown page
 *       plants: [{ plantCode, plantTitle, liaison,
 *                  weeks: [{ weekStart, standard:[7], deviation:[7], ot:[7] }],
 *                  productionDaysBilled, standardTotal, deviationTotal,
 *                  otTotal, plantTotal }],
 *       (`deviation` is the signed +/- against the supplier's standard
 *        hours, shown as an "Overages / Reductions" row; optional on
 *        older payloads)
 *     },
 *     total: number,
 *     isCreditMemo: boolean,
 *   }
 *
 * The function also accepts the legacy (invoice, lineItems) signature.
 *
 * Layout — portrait letter (8.5" × 11"), matching the approved design:
 *   • Thin orange bar across the very top of every page
 *   • Header: Proxemics logo + company contact column on the left;
 *     "INVOICE", the orange invoice number, and the Invoice Date / Due
 *     Date / Billing Period metadata on the right
 *   • Parties band: "Bill To" panel (customer name / address / Attn) and
 *     an "Invoice Details" panel (Account # / PO Number / Payment Terms)
 *   • Line items grouped by plant. Each plant banner carries a code
 *     chip, the plant name (orange), the liaison, and the plant's
 *     Standard Coverage. Rows are Description / Dates / Qty / Rate /
 *     Amount, with an optional explanation note beneath a row. No
 *     per-plant subtotals.
 *   • Totals: a Payment / wire-info column on the left; Subtotal, an
 *     optional Credit (+ note), and a boxed Total Due on the right.
 *   • A right-aligned "Thank you for your partnership." closer.
 *   • Optional "Weekly Hours Summary" appendix: per-plant week grids of
 *     daily liaison hours (Standard / Overages / Reductions / OT / Daily
 *     Total) plus Production Days Billed.
 */
export async function exportInvoicePdf(payload, lineItemsLegacy) {
  const normalized = normalizePayload(payload, lineItemsLegacy);
  // Portrait letter (8.5" × 11") — matches the customer-facing design.
  const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });

  let logo = null;
  try {
    logo = await loadLogo();
  } catch (err) {
    // Non-fatal — the PDF still renders with a text-only wordmark
    // fallback so a flaky network doesn't block the download.
    // eslint-disable-next-line no-console
    console.warn('[pdfExport] logo load failed; continuing without it', err);
  }

  renderInvoice(doc, normalized, logo);

  doc.save(`${normalized.invoice.InvoiceNumber || 'invoice'}.pdf`);
}

// ============================================================
//  Layout constants — portrait letter (8.5" × 11")
// ============================================================
const PAGE_W = 612; // portrait letter width  (8.5 × 72)
const PAGE_H = 792; // portrait letter height (11 × 72)
const MARGIN_L = 45;
const MARGIN_R = 45;
const RIGHT_X = PAGE_W - MARGIN_R; // 567
const CONTENT_W = RIGHT_X - MARGIN_L; // 522

// Brand palette — RGB triples so setFillColor / setTextColor can be
// called with the spread operator.
const ORANGE = [232, 119, 34]; // #E87722
const INK = [29, 27, 24]; // #1D1B18 body-heading text
const SOFT_INK = [60, 58, 54]; // body text
const MUTED = [110, 106, 98]; // #6E6A62 muted labels
const FAINT = [156, 151, 140]; // #9C978C faint labels
const LINE = [228, 229, 231]; // #E4E5E7 row dividers
const LINE_STRONG = [210, 212, 214]; // #D2D4D6
const PANEL = [245, 246, 247]; // #F5F6F7 panel background
const ACCENT_SOFT = [251, 238, 222]; // #FBEEDE total-due box fill
const WHITE = [255, 255, 255];
const TITLE_GRAY = [176, 176, 179]; // light gray for the "INVOICE" title

const TOP_BAR_H = 8;
const BODY_FONT = 9;
const BODY_LINE_H = 13;

// Five-column line-item grid. Left-aligned columns set x = where text
// starts; right-aligned columns set x = where text ends.
const COL = {
  desc: { x: MARGIN_L, align: 'left' },
  dates: { x: 300, align: 'left' },
  qty: { x: 415, align: 'right' },
  rate: { x: 490, align: 'right' },
  total: { x: RIGHT_X, align: 'right' },
};
// Width budget for the Description column before it collides with Dates.
const DESC_MAX_W = COL.dates.x - COL.desc.x - 8;
// The explanation note wraps across the whole content width.
const EXPL_MAX_W = CONTENT_W;

const FOOTER_BAND_TOP = 726; // stop drawing body content below this y

// ============================================================
//  Logo loader (cached across calls)
// ============================================================
const LOGO_URL = '/Proxemics-Logo.png';
let logoPromise = null;
function loadLogo() {
  if (logoPromise) return logoPromise;
  logoPromise = new Promise((resolve, reject) => {
    // eslint-disable-next-line no-undef
    const img = new Image();
    img.onload = () => {
      // Downscale to display resolution before embedding. The logo only
      // renders ~1.9in wide (~140pt), so a ~560px-wide copy is more than
      // sharp enough at print DPI. The source PNG is ~1770px wide, which
      // — embedded raw by jsPDF — would bloat the PDF to ~5.6MB. Capping
      // the pixel size (plus deflate compression on addImage) keeps the
      // whole invoice well under a few hundred KB.
      const MAX_W = 560;
      const scale = Math.min(1, MAX_W / img.naturalWidth);
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      try {
        resolve({
          dataUrl: canvas.toDataURL('image/png'),
          width: w,
          height: h,
        });
      } catch (err) {
        logoPromise = null;
        reject(err);
      }
    };
    img.onerror = (err) => {
      logoPromise = null;
      reject(err);
    };
    img.src = LOGO_URL;
  });
  return logoPromise;
}

// ============================================================
//  Top-level rendering
// ============================================================
function renderInvoice(doc, payload, logo) {
  const { invoice, periodLabel, customer, isCreditMemo, total } = payload;

  // Separate adjustments (plantCode '—') from regular plant groups so
  // adjustments always render at the very bottom.
  const sourceGroups =
    payload.linesByPlant && payload.linesByPlant.length > 0
      ? payload.linesByPlant
      : groupLinesByPlant(payload.lineItems);

  const plantGroups = [];
  const adjustmentLines = [];
  // On-Call Engagement Fees print in their own section (like
  // Adjustments) no matter which plant they belong to.
  const feeLines = [];
  for (const [plantCode, allLines] of sourceGroups) {
    const lines = [];
    for (const li of allLines) {
      if (li.section === 'onCallFee') feeLines.push(li);
      else lines.push(li);
    }
    if (lines.length === 0) continue;
    if (plantCode === '—') {
      // The '—' bucket holds unattributed adjustments. Positive ones
      // (supplements / PPA) render as their own "Adjustments" line and
      // count toward the subtotal. Credits (negative) are summarized in
      // the totals "Credit" line instead, so they're not duplicated
      // here.
      for (const li of lines) {
        const isCredit =
          li.coverageType === 'Credit' || (Number(li.amount) || 0) < 0;
        if (!isCredit) adjustmentLines.push(li);
      }
    } else {
      plantGroups.push([plantCode, lines]);
    }
  }

  // ---- Page 1 header + parties ----
  drawTopBar(doc);
  drawHeaderBand(doc, logo, invoice, periodLabel, isCreditMemo);
  const partiesEndY = drawPartiesBand(doc, invoice, customer);

  // ---- Column headers ----
  let y = partiesEndY + 26;
  y = drawColumnHeaders(doc, y);

  // ---- Plant groups ----
  for (let i = 0; i < plantGroups.length; i += 1) {
    const [plantCode, plantLines] = plantGroups[i];
    const groupH = estimateGroupHeight(doc, plantLines);
    if (y + groupH > FOOTER_BAND_TOP) {
      doc.addPage();
      drawTopBar(doc);
      drawContinuationHeader(doc, logo, invoice);
      y = 120;
      y = drawColumnHeaders(doc, y);
    }
    y = drawPlantGroup(doc, y, plantCode, plantLines, customer);
  }

  // ---- Bottom band (adjustments + totals + payment + thanks) ----
  // Adjustments render inline; the Subtotal/Credit/Total-Due stack plus
  // the thank-you closer need ~150pt beneath them.
  if (feeLines.length > 0) {
    const feeH = estimateGroupHeight(doc, feeLines);
    if (y + feeH > FOOTER_BAND_TOP) {
      doc.addPage();
      drawTopBar(doc);
      drawContinuationHeader(doc, logo, invoice);
      y = 120;
      y = drawColumnHeaders(doc, y);
    }
    y = drawAdjustmentsGroup(doc, y, feeLines, customer, 'On-Call Engagement Fees');
  }
  const bottomNeeded =
    (adjustmentLines.length > 0
      ? estimateGroupHeight(doc, adjustmentLines)
      : 0) + 125;
  if (y + bottomNeeded > FOOTER_BAND_TOP) {
    doc.addPage();
    drawTopBar(doc);
    drawContinuationHeader(doc, logo, invoice);
    y = 120;
    y = drawColumnHeaders(doc, y);
  }

  if (adjustmentLines.length > 0) {
    y = drawAdjustmentsGroup(doc, y, adjustmentLines, customer);
  }

  y += 8;
  drawTotalsAndPayment(doc, y, payload);

  // ---- Appendix (Hours Breakdown) ----
  if (payload.appendix && payload.appendix.plants && payload.appendix.plants.length > 0) {
    renderAppendix(doc, payload, logo);
  }

  // ---- Footer on every page ----
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    drawFooter(doc, invoice, p, pageCount);
  }
}

// ============================================================
//  Header band — page 1 (logo + contact left, INVOICE + meta right)
// ============================================================
function drawHeaderBand(doc, logo, invoice, periodLabel, isCreditMemo) {
  // ---- Logo (or text fallback) ----
  let contactTop;
  if (logo) {
    const logoHeight = 58;
    const logoWidth = (logo.width / logo.height) * logoHeight;
    doc.addImage(logo.dataUrl, 'PNG', MARGIN_L, TOP_BAR_H + 16, logoWidth, logoHeight, 'proxLogo', 'SLOW');
    contactTop = TOP_BAR_H + 16 + logoHeight + 18;
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.setTextColor(...ORANGE);
    doc.text('prox', MARGIN_L, TOP_BAR_H + 48);
    const proxW = doc.getTextWidth('prox');
    doc.setTextColor(...INK);
    doc.text('emics', MARGIN_L + proxW, TOP_BAR_H + 48);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...FAINT);
    doc.text('CONSULTING', MARGIN_L + 2, TOP_BAR_H + 60);
    contactTop = TOP_BAR_H + 78;
  }

  // ---- Company contact column, under the logo ----
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  let cy = contactTop;
  const contactLines = [
    '12451 Flora Drive, Brighton, MI 48114',
    'invoices@proxemicsconsulting.com',
    '(810) 599-9688',
    'Tax ID: 75-3246062',
  ];
  for (const l of contactLines) {
    doc.text(l, MARGIN_L, cy);
    cy += 14;
  }

  // ---- INVOICE title (right) ----
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(...TITLE_GRAY);
  const title = isCreditMemo ? 'CREDIT MEMO' : 'INVOICE';
  doc.text(title, RIGHT_X, TOP_BAR_H + 58, { align: 'right' });

  // ---- Invoice number (orange, right) ----
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...ORANGE);
  doc.text(String(invoice.InvoiceNumber || '—'), RIGHT_X, TOP_BAR_H + 76, {
    align: 'right',
  });

  // Supplemental subtitle (muted) just under the number.
  let metaTop = TOP_BAR_H + 100;
  if (invoice.ParentInvoiceNumber && !isCreditMemo) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    doc.text(`Supplemental to ${invoice.ParentInvoiceNumber}`, RIGHT_X, metaTop, {
      align: 'right',
    });
    metaTop += 15;
  }

  // ---- Metadata rows (label + value, right-aligned) ----
  const serviceDates =
    formatServiceDateRange(invoice.PeriodStart, invoice.PeriodEnd) ||
    periodLabel ||
    '';
  const rows = [
    { label: 'Invoice Date', value: formatDateForMeta(invoice.IssueDate) },
    { label: 'Due Date', value: formatDateForMeta(invoice.DueDate) },
    { label: 'Billing Period', value: serviceDates },
  ];
  const valueX = RIGHT_X;
  const labelX = RIGHT_X - 92; // labels right-aligned here, values to the right
  let my = metaTop;
  for (const r of rows) {
    if (!String(r.value || '').trim()) continue;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    doc.text(r.label, labelX, my, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...INK);
    doc.text(String(r.value), valueX, my, { align: 'right' });
    my += 14;
  }
}

// A slim header for continuation / appendix-adjacent pages: just the
// logo and the invoice number, no full metadata block.
function drawContinuationHeader(doc, logo, invoice) {
  if (logo) {
    const logoHeight = 34;
    const logoWidth = (logo.width / logo.height) * logoHeight;
    doc.addImage(logo.dataUrl, 'PNG', MARGIN_L, TOP_BAR_H + 14, logoWidth, logoHeight, 'proxLogo', 'SLOW');
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...ORANGE);
    doc.text('prox', MARGIN_L, TOP_BAR_H + 34);
    const proxW = doc.getTextWidth('prox');
    doc.setTextColor(...INK);
    doc.text('emics', MARGIN_L + proxW, TOP_BAR_H + 34);
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(`Invoice ${invoice.InvoiceNumber || ''}`, RIGHT_X, TOP_BAR_H + 34, {
    align: 'right',
  });
}

// ============================================================
//  Parties band — Bill To + Invoice Details panels
// ============================================================
function drawPartiesBand(doc, invoice, customer) {
  const top = 172;
  const gap = 22;
  const panelW = (CONTENT_W - gap) / 2;
  const leftX = MARGIN_L;
  const rightX = MARGIN_L + panelW + gap;

  // ---- Content prep ----
  const customerName = invoice.CustomerName || customer?.Title || '';
  const addressLines = composeAddressLines(invoice, customer);
  // Only the Main Billing Contact prints (Oct 2026). Additional Billing
  // Contacts are email recipients, never shown on the invoice. Older
  // invoices that stored several names print just the first.
  const mainContact = invoice.billingContacts?.main || null;
  const mainName = stripContactTitle(
    mainContact?.name || (Array.isArray(invoice.contactNames) ? invoice.contactNames[0] : ''),
  );
  const contacts = mainName
    ? [
        mainName,
        String(mainContact?.email || '').trim(),
        String(mainContact?.phone || '').trim(),
      ].filter(Boolean)
    : [];
  const accountNo = customer?.SupplierCode || invoice.SupplierCode || '';
  const poNumber = String(invoice.PoNumber || '').trim();
  const paymentTerms = String(invoice.PaymentTerms || 'Net 60').trim();

  // ---- Measure heights so both panels share the taller height ----
  const pad = 12;
  // Left: eyebrow + name + address lines + (divider + attn) + (email)
  let leftInner = 14 /* eyebrow */ + 14 /* name */;
  leftInner += addressLines.length * 12;
  if (contacts.length > 0) leftInner += 10 /* divider gap */ + contacts.length * 12;
  // Right: eyebrow + 3 kv rows
  const rightRows = [
    ['Account #', accountNo],
    ['PO Number', poNumber],
    ['Payment Terms', paymentTerms],
  ].filter((r) => String(r[1] || '').trim());
  let rightInner = 14 + rightRows.length * 16;
  const panelH = Math.max(leftInner, rightInner) + pad * 2;

  // ---- Panel backgrounds ----
  doc.setFillColor(...PANEL);
  doc.roundedRect(leftX, top, panelW, panelH, 4, 4, 'F');
  doc.roundedRect(rightX, top, panelW, panelH, 4, 4, 'F');

  // ---- Left panel: Bill To ----
  let ly = top + pad + 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text('BILL TO', leftX + pad, ly);
  ly += 15;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.text(customerName, leftX + pad, ly);
  ly += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...SOFT_INK);
  for (const l of addressLines) {
    doc.text(l, leftX + pad, ly);
    ly += 12;
  }
  if (contacts.length > 0) {
    ly += 2;
    doc.setDrawColor(...LINE_STRONG);
    doc.setLineWidth(0.5);
    doc.line(leftX + pad, ly, leftX + panelW - pad, ly);
    ly += 12;
    doc.setFontSize(9);
    for (let i = 0; i < contacts.length; i += 1) {
      const prefix = i === 0 ? 'Attn: ' : '';
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...INK);
      doc.text(prefix, leftX + pad, ly);
      const pw = doc.getTextWidth(prefix);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...SOFT_INK);
      doc.text(String(contacts[i]), leftX + pad + pw, ly);
      ly += 12;
    }
  }

  // ---- Right panel: Invoice Details ----
  let ry = top + pad + 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text('INVOICE DETAILS', rightX + pad, ry);
  ry += 17;
  for (const [label, value] of rightRows) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(label, rightX + pad, ry);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    doc.text(String(value), rightX + panelW - pad, ry, { align: 'right' });
    ry += 16;
  }

  return top + panelH;
}

// ============================================================
//  Column headers
// ============================================================
function drawColumnHeaders(doc, y) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text('DESCRIPTION', COL.desc.x, y);
  doc.text('DATES', COL.dates.x, y);
  doc.text('QTY', COL.qty.x, y, { align: 'right' });
  doc.text('RATE', COL.rate.x, y, { align: 'right' });
  doc.text('AMOUNT', COL.total.x, y, { align: 'right' });
  // Strong rule under the header.
  doc.setDrawColor(...INK);
  doc.setLineWidth(1);
  doc.line(MARGIN_L, y + 6, RIGHT_X, y + 6);
  return y + 20;
}

// ============================================================
//  Plant group — banner + line items (no subtotal)
// ============================================================
function drawPlantGroup(doc, startY, plantCode, plantLines, customer) {
  let y = startY + 8;

  const plantTitle =
    (plantLines && plantLines.find((li) => li.plantTitle)?.plantTitle) ||
    plantCode ||
    '';
  const coverage = coverageLabelFor(customer, plantCode);

  // ---- Banner: code chip + plant name (left); coverage (right) ----
  // The liaison's name used to sit beside Coverage; removed from the
  // invoice page per Jillian, Oct 2026 (it still shows on the Daily
  // Hours Summary appendix).
  // Code chip.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  const chipText = String(plantCode || '').toUpperCase();
  const chipTextW = doc.getTextWidth(chipText);
  const chipPadX = 6;
  const chipW = chipTextW + chipPadX * 2;
  const chipH = 13;
  doc.setFillColor(...ORANGE);
  doc.roundedRect(COL.desc.x, y - 10, chipW, chipH, 3, 3, 'F');
  doc.setTextColor(...WHITE);
  doc.text(chipText, COL.desc.x + chipPadX, y);

  // Plant name (orange, bold).
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...ORANGE);
  doc.text(plantTitle, COL.desc.x + chipW + 8, y);

  // Right side: "Coverage X" anchored at the right margin.
  if (coverage) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const covValue = coverage;
    const covLabel = 'Coverage ';
    doc.setFont('helvetica', 'bold');
    const covValueW = doc.getTextWidth(covValue);
    doc.setFont('helvetica', 'normal');
    const covLabelW = doc.getTextWidth(covLabel);
    const covStartX = RIGHT_X - covValueW - covLabelW;
    doc.setTextColor(...MUTED);
    doc.text(covLabel, covStartX, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    doc.text(covValue, covStartX + covLabelW, y);
  }

  y += 14;

  // ---- Line rows ----
  // Weekly labor lines are rolled up into one Standard line (plus one
  // "Overages or Reductions" line listing the off-standard days) per
  // service and rate, so a plant reads as a single itemized section
  // instead of one row per week (per Jillian, Oct 2026).
  for (const li of consolidatePlantLines(plantLines)) {
    y = drawLineRow(doc, y, li);
  }

  return y + 6;
}

// ============================================================
//  Roll weekly labor lines into one section per service / rate
// ============================================================
//
// Input: the draft's line items for one plant (one per week per rate).
// Output: the rows the PDF prints, in order —
//   • "Standard Resident Support Hours (Production Days: N)" — the hours
//     the supplier's standard called for across the billed days, at the
//     rate, spanning the whole period;
//   • "Overages or Reductions" — the net hours over / under standard,
//     signed, with one sub-row per off-standard day (date, over/under,
//     the day's note, and that day's +/- hours in the Qty column);
//   • "OT Resident Support Hours" — all OT weeks rolled up, with one
//     sub-row per day worked;
//   • everything else (expenses, mileage, no-rate lines, hand-added
//     lines, lines from invoices saved before day detail existed with
//     nothing to roll up) passes through unchanged.
function consolidatePlantLines(plantLines) {
  const out = [];
  const groups = new Map(); // key → accumulator, insertion-ordered
  const passthrough = [];
  for (const li of plantLines || []) {
    const ct = li.coverageType || 'Standard';
    const isLabor =
      li.category === 'Liaison Support' &&
      (ct === 'Standard' || ct === 'Overage') &&
      li.unitLabel === 'hrs' &&
      !li.orphan &&
      // A line the Director hand-edited prints exactly as typed.
      !li.edited &&
      li.rate != null;
    if (!isLabor) {
      passthrough.push(li);
      continue;
    }
    const isOT = ct === 'Overage';
    const key = `${isOT ? 'ot' : 'std'}|${li.service || ''}|${Number(li.rate)}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        isOT,
        service: li.service || '',
        rate: Number(li.rate),
        hours: 0,
        amount: 0,
        start: '',
        end: '',
        days: [],
        workedDays: new Set(),
        notes: [], // legacy / hand-edited notes, with their week range
        hasDayDetail: false,
        sample: li,
      };
      groups.set(key, g);
    }
    g.hours += Number(li.quantity) || 0;
    g.amount += Number(li.amount) || 0;
    const s0 = li.dateRangeStart || li.weekStart || '';
    const e0 = li.dateRangeEnd || s0;
    if (s0 && (!g.start || s0 < g.start)) g.start = s0;
    if (e0 && (!g.end || e0 > g.end)) g.end = e0;
    const dd = Array.isArray(li.dayDetails) ? li.dayDetails : [];
    if (dd.length) g.hasDayDetail = true;
    for (const d of dd) g.days.push({ ...d, _week: s0 });
    for (const wd of Array.isArray(li.workedDays) ? li.workedDays : []) g.workedDays.add(wd);
    const noteText = String(li.details || '').trim();
    if (noteText && (dd.length === 0 || li.detailsEdited)) {
      g.notes.push({ range: formatDateRangeShort(s0, e0), text: noteText });
    }
  }

  for (const g of groups.values()) {
    const base = g.sample;
    const serviceSuffix = g.service && !/^standard coverage$/i.test(g.service) ? ` — ${g.service}` : '';
    // Sort day sub-rows chronologically.
    g.days.sort((a, b) => String(a.date || a._week || '').localeCompare(String(b.date || b._week || '')));
    // Legacy notes: only prefix with the week when more than one week
    // contributed, so a single-week note reads exactly as before.
    const mergedNote =
      g.notes.length === 0
        ? ''
        : g.notes.length === 1
          ? g.notes[0].text
          : g.notes.map((n) => `${n.range}: ${n.text}`).join('\n');

    if (g.isOT) {
      out.push({
        ...base,
        description: 'OT Resident Support Hours' + serviceSuffix,
        dateRangeStart: g.start,
        dateRangeEnd: g.end,
        quantity: g.hours,
        amount: g.amount,
        dayDetails: g.days,
        details: mergedNote,
        detailsEdited: g.notes.length > 0,
      });
      continue;
    }

    const netDelta = g.days.reduce((acc, d) => acc + (Number(d.delta) || 0), 0);
    const standardHours = Math.round((g.hours - netDelta) * 100) / 100;
    const productionDays = g.workedDays.size;
    out.push({
      ...base,
      description: 'Standard Resident Support Hours' + serviceSuffix,
      descSuffix: productionDays > 0 ? `Production Days: ${productionDays}` : '',
      dateRangeStart: g.start,
      dateRangeEnd: g.end,
      quantity: standardHours,
      amount: Math.round(standardHours * g.rate * 100) / 100,
      dayDetails: [],
      details: g.hasDayDetail ? '' : mergedNote,
      detailsEdited: !g.hasDayDetail && g.notes.length > 0,
    });
    if (g.days.length > 0) {
      const devStart = g.days[0].date || g.start;
      const devEnd = g.days[g.days.length - 1].date || g.end;
      out.push({
        ...base,
        description: 'Overages / Reductions' + serviceSuffix,
        dateRangeStart: devStart,
        dateRangeEnd: devEnd,
        quantity: Math.round(netDelta * 100) / 100,
        signedQty: true,
        amount: Math.round(netDelta * g.rate * 100) / 100,
        dayDetails: g.days,
        details: mergedNote,
        detailsEdited: g.notes.length > 0,
        isDeviationLine: true,
      });
    }
  }
  return out.concat(passthrough);
}

// ============================================================
//  Adjustments group — same shape, banner reads "Adjustments"
// ============================================================
function drawAdjustmentsGroup(doc, startY, adjustmentLines, customer, title = 'Adjustments') {
  let y = startY + 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...INK);
  doc.text(title, COL.desc.x, y);
  y += 14;
  for (const li of adjustmentLines) {
    y = drawLineRow(doc, y, li);
  }
  return y + 6;
}

// ============================================================
//  Single line item row (+ optional explanation note)
// ============================================================
function drawLineRow(doc, y, li) {
  const desc = li.description || '';
  const details = li.details || '';
  const dates = li.dateRangeStart
    ? formatDateRangeShort(li.dateRangeStart, li.dateRangeEnd)
    : li.weekStart
      ? formatWeekRangeShort(li.weekStart)
      : '—';
  const isExpense = li.coverageType === 'Expenses' || li.category === 'Expenses';
  const qtyNum = Number(li.quantity) || 0;
  const qty = isExpense || (!qtyNum && !li.signedQty)
    ? '—'
    : li.unitLabel === 'hrs'
      ? li.signedQty
        ? `${qtyNum > 0 ? '+' : qtyNum < 0 ? '-' : ''}${Math.abs(qtyNum).toFixed(1)} hrs`
        : `${qtyNum.toFixed(1)} hrs`
      : `${formatQuantity(li.quantity)}${li.unitLabel ? ` ${li.unitLabel}` : ''}`;
  const rate = li.rate != null ? formatCurrency(li.rate) : '—';
  const amount = formatSignedCurrency(li.amount);

  // Description (bold ink), wrapped inside its column so a long service
  // name never runs into the Dates column, + optional "1.5x rate" tag
  // for OT lines and an optional muted suffix ("Production Days: 20").
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(BODY_FONT);
  doc.setTextColor(...INK);
  const descLines = doc.splitTextToSize(desc, DESC_MAX_W);
  let descY = y;
  for (let i = 0; i < descLines.length; i += 1) {
    doc.text(descLines[i], COL.desc.x, descY);
    if (i < descLines.length - 1) descY += 11;
  }
  const lastDescW = doc.getTextWidth(descLines[descLines.length - 1] || '');
  let afterX = COL.desc.x + lastDescW + 6;
  const isOT = li.coverageType === 'Overage';
  if (isOT) {
    drawTag(doc, afterX, descY, '1.5x rate');
    doc.setFontSize(6.5);
    afterX += doc.getTextWidth('1.5x rate') + 8 + 6;
    doc.setFontSize(BODY_FONT);
  }
  const suffix = String(li.descSuffix || '').trim();
  if (suffix) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    const sufW = doc.getTextWidth(suffix);
    if (afterX + sufW <= COL.desc.x + DESC_MAX_W) {
      doc.text(suffix, afterX, descY);
    } else {
      descY += 10;
      doc.text(suffix, COL.desc.x, descY);
    }
  }
  // Everything beneath the row starts under the last description line.
  const descExtra = descY - y;

  // Data cells (normal).
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(BODY_FONT);
  doc.setTextColor(...SOFT_INK);
  doc.text(dates, COL.dates.x, y);
  doc.text(qty, COL.qty.x, y, { align: 'right' });
  doc.text(rate, COL.rate.x, y, { align: 'right' });
  doc.setTextColor(...INK);
  doc.text(amount, COL.total.x, y, { align: 'right' });

  // Beneath the line: either the itemized day list (per Jillian, Oct
  // 2026) or the legacy flat explanation note. The day list keeps the
  // total on the line above and prints one sub-row per day that ran
  // over / under standard (or, for OT, every day worked): the day,
  // its hours in the Qty column, how far off standard, and the note.
  let cursor;
  const dayDetails = Array.isArray(li.dayDetails) ? li.dayDetails : [];
  const hasDays = dayDetails.length > 0;
  const noteText = String(details || '').trim();
  // The flat note repeats the day list, so it's only printed when there
  // is no day list, or when the Director hand-edited it on the draft.
  const showNote = noteText && (!hasDays || li.detailsEdited);
  if (hasDays || showNote) {
    cursor = y + descExtra + 11;
  } else {
    cursor = y + descExtra + 7;
  }
  if (hasDays) {
    cursor = drawDayDetails(doc, cursor, dayDetails, isOT);
  }
  if (showNote) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    const noteLines = doc.splitTextToSize(noteText, EXPL_MAX_W);
    for (const nl of noteLines) {
      doc.text(nl, COL.desc.x, cursor);
      cursor += 10;
    }
    cursor += 1;
  } else if (hasDays) {
    cursor += 1;
  }

  // Divider under the row (carries beneath the note when present).
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_L, cursor, RIGHT_X, cursor);

  return cursor + 12;
}

// Itemized day sub-rows under a labor line. Each prints as:
//   TU 09/22   +2.0 hrs over standard · Extra coverage for launch build       6.0 hrs
// with the day's hours right-aligned in the Qty column. OT days have no
// standard to compare against, so they print the day and note only.
const DAY_SUB_FONT = 8;
const DAY_SUB_LINE_H = 10;
// Day rows sit flush with the Description column (no indent) per
// Jillian, Oct 2026.
const DAY_SUB_INDENT = 0;
// Fixed width for the day label ("TU 09/22") so the text after it lines
// up from row to row regardless of the label's own width.
const DAY_SUB_LABEL_W = 44;
// Text in the description area stops short of the Qty column.
const DAY_SUB_TEXT_MAX_W = COL.qty.x - 48 - (COL.desc.x + DAY_SUB_INDENT);

function dayDetailParts(d, isOT) {
  const note = String(d.note || '').trim();
  if (isOT) {
    const h = Number(d.hours) || 0;
    return { label: String(d.label || ''), hoursText: `${h.toFixed(1)} hrs`, offText: '', note };
  }
  // Standard deviation day: the Qty column shows the +/- hours against
  // standard (what the Overages or Reductions line sums to). The sign
  // alone says which way it went, so no "Over"/"Under" word is printed.
  // ASCII hyphen on purpose: the PDF's built-in Helvetica has no glyph
  // for the typographic minus sign.
  const delta = Number(d.delta) || 0;
  const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';
  const hoursText = `${sign}${Math.abs(delta).toFixed(1)} hrs`;
  return { label: String(d.label || ''), hoursText, offText: '', note };
}

function dayDetailLineCount(doc, d, isOT) {
  const { label, offText, note } = dayDetailParts(d, isOT);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(DAY_SUB_FONT);
  const rest = [offText, note].filter(Boolean).join(' · ');
  if (!rest) return 1;
  return Math.max(1, doc.splitTextToSize(rest, DAY_SUB_TEXT_MAX_W - DAY_SUB_LABEL_W).length);
}

function drawDayDetails(doc, startY, dayDetails, isOT) {
  let cursor = startY;
  const x = COL.desc.x + DAY_SUB_INDENT;
  for (const d of dayDetails) {
    const { label, hoursText, offText, note } = dayDetailParts(d, isOT);
    const lineY = cursor;
    // Day label (bold) then the rest (muted), wrapped as one paragraph
    // so a long note continues under the label on following lines.
    // Day label in the same regular weight and gray as the rest of the
    // row; the fixed label column keeps the text aligned.
    doc.setFontSize(DAY_SUB_FONT);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    doc.text(label, x, cursor);
    const rest = [offText, note].filter(Boolean).join(' · ');
    if (rest) {
      const textX = x + DAY_SUB_LABEL_W;
      const textW = DAY_SUB_TEXT_MAX_W - DAY_SUB_LABEL_W;
      const lines = doc.splitTextToSize(rest, Math.max(80, textW));
      doc.text(lines[0], textX, cursor);
      // Any continuation lines wrap beneath, aligned under the text.
      for (let i = 1; i < lines.length; i += 1) {
        cursor += DAY_SUB_LINE_H;
        doc.text(lines[i], textX, cursor);
      }
    }
    // Hours for the day, right-aligned under the Qty column.
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...SOFT_INK);
    doc.text(hoursText, COL.qty.x, lineY, { align: 'right' });
    cursor += DAY_SUB_LINE_H;
  }
  return cursor;
}

// Small outlined "1.5× rate" tag next to OT descriptions.
function drawTag(doc, x, baselineY, text) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  const padX = 4;
  const w = doc.getTextWidth(text) + padX * 2;
  const h = 10;
  const boxY = baselineY - 8;
  doc.setDrawColor(...ORANGE);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, boxY, w, h, 2, 2, 'S');
  doc.setTextColor(...ORANGE);
  doc.text(text, x + padX, boxY + h - 3);
}

// ============================================================
//  Totals + payment block
// ============================================================
function drawTotalsAndPayment(doc, startY, payload) {
  const { total, isCreditMemo } = payload;
  const lineItems = flattenLines(payload);

  // Subtotal excludes credit/negative adjustment lines; credits render
  // on their own line below.
  const creditLines = lineItems.filter(
    (li) => li.coverageType === 'Credit' || (Number(li.amount) || 0) < 0,
  );
  const subtotal = lineItems
    .filter((li) => !(li.coverageType === 'Credit' || (Number(li.amount) || 0) < 0))
    .reduce((acc, li) => acc + (Number(li.amount) || 0), 0);
  const creditTotal = creditLines.reduce(
    (acc, li) => acc + (Number(li.amount) || 0),
    0,
  );

  // ---- Right column geometry ----
  const colW = 210;
  const colX = RIGHT_X - colW;
  let y = startY;

  // Subtotal row.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text('Subtotal', colX, y);
  doc.setTextColor(...INK);
  doc.text(formatSignedCurrency(subtotal), RIGHT_X, y, { align: 'right' });
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.5);
  doc.line(colX, y + 6, RIGHT_X, y + 6);
  y += 20;

  // Credit row + note (if any).
  if (creditLines.length > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text('Credit', colX, y);
    doc.setTextColor(...ORANGE);
    doc.text(formatSignedCurrency(creditTotal), RIGHT_X, y, { align: 'right' });
    y += 12;
    const note = creditLines
      .map((li) => String(li.details || '').trim())
      .filter(Boolean)
      .join('; ');
    if (note) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...MUTED);
      const noteLines = doc.splitTextToSize(note, colW);
      for (const nl of noteLines) {
        doc.text(nl, colX, y);
        y += 10;
      }
    }
    y += 6;
  }

  // Total Due box (accent-soft fill, orange border, vertically centered).
  const boxH = 40;
  const boxY = y;
  doc.setFillColor(...ACCENT_SOFT);
  doc.setDrawColor(...ORANGE);
  doc.setLineWidth(0.8);
  doc.roundedRect(colX, boxY, colW, boxH, 6, 6, 'FD');
  const midY = boxY + boxH / 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...ORANGE);
  doc.text(isCreditMemo ? 'TOTAL CREDIT' : 'TOTAL DUE', colX + 12, midY + 3);
  doc.setFontSize(17);
  doc.text(formatSignedCurrency(total), RIGHT_X - 12, midY + 5, { align: 'right' });

  // ---- Left column: Payment / wire info ----
  drawPaymentColumn(doc, startY, colX - 24, payload.invoice);

  // ---- Thank-you closer, right-aligned under the box ----
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...ORANGE);
  doc.text('Thank you for your partnership.', RIGHT_X, boxY + boxH + 22, {
    align: 'right',
  });
}

function drawPaymentColumn(doc, startY, maxRightX, invoice) {
  const x = MARGIN_L;
  const maxW = maxRightX - x;
  let y = startY;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text('PAYMENT', x, y);
  y += 14;

  // Wording per Jillian, Oct 2026: one short instruction, then the bank
  // and account name, then the three numbers. The instruction stays
  // muted; the bank block renders in ink with the numbers bold.
  const paras = [
    { text: 'Please include invoice number with payment.', color: MUTED },
    {
      // Each line is "Label: Value" — the label stays normal weight,
      // the value renders bold. An empty label prints the value alone.
      color: INK,
      mixed: [
        ['', 'JP Morgan Chase Bank'],
        ['', 'Proxemics Consulting Inc.'],
        ['Account: ', '362988997'],
        ['ACH Routing: ', '072000326'],
        ['Wire Routing: ', '021000021'],
      ],
    },
  ];
  doc.setFontSize(8.5);
  for (const p of paras) {
    doc.setTextColor(...p.color);
    // Mixed-weight block: draw label (normal) + value (bold) per line.
    if (p.mixed) {
      for (const [label, value] of p.mixed) {
        doc.setFont('helvetica', 'normal');
        let lw = 0;
        if (label) {
          doc.text(label, x, y);
          lw = doc.getTextWidth(label);
        }
        // Bank and account name read as plain ink; the numbers are bold.
        doc.setFont('helvetica', label ? 'bold' : 'normal');
        doc.text(value, x + lw, y);
        y += 11;
      }
      y += 5;
      continue;
    }
    doc.setFont('helvetica', 'normal');
    for (const sub of String(p.text).split('\n')) {
      const wrapped = doc.splitTextToSize(sub, maxW);
      for (const w of wrapped) {
        doc.text(w, x, y);
        y += 11;
      }
    }
    y += 5;
  }
}

// ============================================================
//  Appendix — Weekly Hours Summary page
// ============================================================
function renderAppendix(doc, payload, logo) {
  const { appendix, customer } = payload;
  doc.addPage();
  let y = drawAppendixHeader(doc, payload, logo);

  for (const plant of appendix.plants) {
    // Page-break if the plant block won't fit. Mirrors drawAppendixPlant:
    // banner (16) + each week grid (header, Standard, optional Overages /
    // Reductions, optional OT, Daily Total rows of 15pt plus an 8pt gap)
    // + the summary strip (36).
    const weeks = plant.weeks || [];
    const gridH = weeks.reduce((acc, wk) => {
      const hasOT = (wk.ot || []).some((v) => (Number(v) || 0) > 0);
      const hasDev = (wk.deviation || []).some((v) => (Number(v) || 0) !== 0);
      return acc + 15 * (3 + (hasOT ? 1 : 0) + (hasDev ? 1 : 0)) + 8;
    }, 0);
    const est = 16 + gridH + 36;
    if (y + est > FOOTER_BAND_TOP) {
      // Every summary page carries the full "Weekly Hours Summary"
      // header (per Jillian, Oct 2026), not the slim continuation one.
      doc.addPage();
      y = drawAppendixHeader(doc, payload, logo);
    }
    y = drawAppendixPlant(doc, y, plant, customer);
    y += 14;
  }
}

// Logo, "Weekly Hours Summary" title and the customer / billing-period
// intro line. Drawn at the top of every summary page; returns the y
// where plant blocks start.
function drawAppendixHeader(doc, payload, logo) {
  const { invoice, customer } = payload;
  drawTopBar(doc);

  // Header: logo left (same size as page 1), title right.
  if (logo) {
    const logoHeight = 58;
    const logoWidth = (logo.width / logo.height) * logoHeight;
    doc.addImage(logo.dataUrl, 'PNG', MARGIN_L, TOP_BAR_H + 16, logoWidth, logoHeight, 'proxLogo', 'SLOW');
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...ORANGE);
    doc.text('prox', MARGIN_L, TOP_BAR_H + 38);
    const proxW = doc.getTextWidth('prox');
    doc.setTextColor(...INK);
    doc.text('emics', MARGIN_L + proxW, TOP_BAR_H + 38);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...INK);
  doc.text('Weekly Hours Summary', RIGHT_X, TOP_BAR_H + 58, { align: 'right' });

  // Intro note.
  const customerName = invoice.CustomerName || customer?.Title || '';
  const period =
    formatServiceDateRange(invoice.PeriodStart, invoice.PeriodEnd) ||
    payload.periodLabel ||
    '';
  const y = TOP_BAR_H + 108;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...SOFT_INK);
  const introBold = customerName;
  const pre = 'Weekly resident support hours for ';
  doc.text(pre, MARGIN_L, y);
  const preW = doc.getTextWidth(pre);
  doc.setFont('helvetica', 'bold');
  doc.text(introBold, MARGIN_L + preW, y);
  const nameW = doc.getTextWidth(introBold);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  doc.text(period ? `  ·  Billing Period ${period}` : '', MARGIN_L + preW + nameW, y);
  return y + 38;
}

function drawAppendixPlant(doc, startY, plant, customer) {
  let y = startY + 4;
  // Plant banner (code chip + name + liaison).
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  const chipText = String(plant.plantCode || '').toUpperCase();
  const chipTextW = doc.getTextWidth(chipText);
  const chipW = chipTextW + 12;
  doc.setFillColor(...ORANGE);
  doc.roundedRect(MARGIN_L, y - 10, chipW, 13, 3, 3, 'F');
  doc.setTextColor(...WHITE);
  doc.text(chipText, MARGIN_L + 6, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...ORANGE);
  doc.text(plant.plantTitle || plant.plantCode || '', MARGIN_L + chipW + 8, y);

  // Right side: Coverage anchored at the right margin (same place as the
  // invoice page), with the liaison just to its left.
  let rightCursor = RIGHT_X;
  const coverage = coverageLabelFor(customer, plant.plantCode);
  if (coverage) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const covLabel = 'Coverage ';
    const covValue = coverage;
    doc.setFont('helvetica', 'bold');
    const covValueW = doc.getTextWidth(covValue);
    doc.setFont('helvetica', 'normal');
    const covLabelW = doc.getTextWidth(covLabel);
    const covStartX = RIGHT_X - covValueW - covLabelW;
    doc.setTextColor(...MUTED);
    doc.text(covLabel, covStartX, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    doc.text(covValue, covStartX + covLabelW, y);
    rightCursor = covStartX - 18;
  }
  if (plant.liaison) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const liLabel = 'Liaison ';
    doc.setTextColor(...MUTED);
    const liValue = plant.liaison;
    doc.setFont('helvetica', 'bold');
    const liValueW = doc.getTextWidth(liValue);
    doc.setFont('helvetica', 'normal');
    const liLabelW = doc.getTextWidth(liLabel);
    const sx = rightCursor - liValueW - liLabelW;
    doc.text(liLabel, sx, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    doc.text(liValue, sx + liLabelW, y);
  }
  y += 12;

  // Week grids.
  for (const wk of plant.weeks || []) {
    y = drawWeekGrid(doc, y, wk);
    y += 8;
  }

  // Plant summary line.
  y += 2;
  doc.setDrawColor(...LINE_STRONG);
  doc.setLineWidth(0.6);
  doc.line(MARGIN_L, y, RIGHT_X, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const devTotal = Number(plant.deviationTotal) || 0;
  const summary = [
    ['Production Days Billed', String(plant.productionDaysBilled ?? 0)],
    ['Standard', `${formatQuantity(plant.standardTotal || 0)} hrs`],
    ['Overages / Reductions', `${formatSignedQuantity(devTotal)} hrs`],
    ['OT', `${formatQuantity(plant.otTotal || 0)} hrs`],
    ['Plant Total', `${formatQuantity(plant.plantTotal || 0)} hrs`],
  ];
  // Cells are sized to their labels so the long ones ("Production Days
  // Billed", "Overages / Reductions") don't run into their neighbours.
  const weights = [1.3, 0.85, 1.3, 0.55, 1.0];
  const weightSum = weights.reduce((a, b) => a + b, 0);
  let sx = MARGIN_L;
  for (let i = 0; i < summary.length; i += 1) {
    const cellW = (CONTENT_W * (weights[i] || 1)) / weightSum;
    const [label, value] = summary[i];
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(label.toUpperCase(), sx, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...(i === summary.length - 1 ? ORANGE : INK));
    doc.text(value, sx, y + 13);
    sx += cellW;
  }
  return y + 20;
}

// A single week grid: Category | SU..SA | Total, with Standard,
// Overages / Reductions (when any day ran over or under), OT (when any)
// and Daily Total rows.
function drawWeekGrid(doc, startY, wk) {
  const catW = 100; // wide enough for "Overages / Reductions"
  const totW = 52;
  const dayW = (CONTENT_W - catW - totW) / 7;
  const x0 = MARGIN_L;
  const dayX0 = x0 + catW;
  const totX = x0 + catW + dayW * 7;
  const rowH = 15;
  let y = startY;

  const dayLetters = ['SU', 'M', 'TU', 'W', 'TH', 'F', 'SA'];
  const dates = weekDatesShort(wk.weekStart);

  // Header row.
  doc.setFillColor(...PANEL);
  doc.rect(x0, y, CONTENT_W, rowH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.text('CATEGORY', x0 + 5, y + 10);
  for (let i = 0; i < 7; i += 1) {
    const cx = dayX0 + dayW * i + dayW / 2;
    doc.setTextColor(...INK);
    doc.text(dayLetters[i], cx, y + 7, { align: 'center' });
    doc.setTextColor(...FAINT);
    doc.setFontSize(6);
    doc.text(dates[i] || '', cx, y + 13, { align: 'center' });
    doc.setFontSize(7);
  }
  doc.setTextColor(...MUTED);
  doc.text('TOTAL', totX + totW - 5, y + 10, { align: 'right' });
  y += rowH;

  const stdTotal = (wk.standard || []).reduce((a, b) => a + (Number(b) || 0), 0);
  const devTotal = (wk.deviation || []).reduce((a, b) => a + (Number(b) || 0), 0);
  const otTotal = (wk.ot || []).reduce((a, b) => a + (Number(b) || 0), 0);
  const hasDev = (wk.deviation || []).some((v) => (Number(v) || 0) !== 0);

  // `signed` rows print +/- values (ASCII hyphen: the built-in Helvetica
  // has no typographic minus) and treat 0 as "nothing happened".
  const drawDataRow = (label, arr, rowTotal, bold, signed = false) => {
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.5);
    doc.line(x0, y, RIGHT_X, y);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...(bold ? INK : SOFT_INK));
    doc.text(label, x0 + 5, y + 10);
    for (let i = 0; i < 7; i += 1) {
      const v = Number(arr?.[i]) || 0;
      const cx = dayX0 + dayW * i + dayW / 2;
      const live = signed ? v !== 0 : v > 0;
      if (live) {
        doc.setTextColor(...(bold ? INK : SOFT_INK));
        const txt = signed ? formatSignedQuantity(v) : formatQuantity(v);
        doc.text(txt, cx, y + 10, { align: 'center' });
      } else {
        doc.setTextColor(...FAINT);
        doc.text('0', cx, y + 10, { align: 'center' });
      }
    }
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    const totTxt = signed ? formatSignedQuantity(rowTotal) : formatQuantity(rowTotal);
    doc.text(totTxt, totX + totW - 5, y + 10, { align: 'right' });
    y += rowH;
  };

  drawDataRow('Standard', wk.standard, stdTotal, false);
  if (hasDev) drawDataRow('Overages / Reductions', wk.deviation, devTotal, false, true);
  if (otTotal > 0) drawDataRow('OT', wk.ot, otTotal, false);

  // Daily total footer row.
  doc.setDrawColor(...LINE_STRONG);
  doc.setLineWidth(0.7);
  doc.line(x0, y, RIGHT_X, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...INK);
  doc.text('Daily Total', x0 + 5, y + 10);
  for (let i = 0; i < 7; i += 1) {
    const v =
      (Number(wk.standard?.[i]) || 0) +
      (Number(wk.deviation?.[i]) || 0) +
      (Number(wk.ot?.[i]) || 0);
    const cx = dayX0 + dayW * i + dayW / 2;
    doc.setTextColor(...(v > 0 ? INK : FAINT));
    doc.text(v > 0 ? formatQuantity(v) : '0', cx, y + 10, { align: 'center' });
  }
  doc.setTextColor(...INK);
  doc.text(formatQuantity(stdTotal + devTotal + otTotal), totX + totW - 5, y + 10, {
    align: 'right',
  });
  y += rowH;

  // Outer border.
  doc.setDrawColor(...LINE_STRONG);
  doc.setLineWidth(0.7);
  doc.rect(x0, startY, CONTENT_W, y - startY);
  return y;
}

// ============================================================
//  Shared bits
// ============================================================
function drawTopBar(doc) {
  doc.setFillColor(...ORANGE);
  doc.rect(0, 0, PAGE_W, TOP_BAR_H, 'F');
}

function drawFooter(doc, invoice, page, total) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...FAINT);
  doc.text(
    `Proxemics Consulting  ·  Invoice ${invoice.InvoiceNumber || ''}`,
    MARGIN_L,
    PAGE_H - 26,
  );
  doc.text(`Page ${page} of ${total}`, RIGHT_X, PAGE_H - 26, { align: 'right' });
}

// ============================================================
//  Helpers
// ============================================================
function normalizePayload(payload, lineItemsLegacy) {
  let invoice;
  let lineItems;
  let linesByPlant;
  let periodLabel = '';
  let customer = null;
  let total = 0;
  let isCreditMemo = false;
  let appendix = null;
  if (payload && payload.invoice) {
    invoice = payload.invoice || {};
    lineItems = payload.lineItems || [];
    linesByPlant = payload.linesByPlant || null;
    periodLabel = payload.periodLabel || '';
    customer = payload.customer || null;
    appendix = payload.appendix || null;
    total =
      typeof payload.total === 'number'
        ? payload.total
        : lineItems.reduce((s, li) => s + (Number(li.amount) || 0), 0);
    isCreditMemo = !!payload.isCreditMemo;
  } else {
    invoice = payload || {};
    lineItems = lineItemsLegacy || [];
    total = lineItems.reduce((s, li) => s + (Number(li.amount) || 0), 0);
  }
  return {
    invoice,
    lineItems,
    linesByPlant,
    periodLabel,
    customer,
    total,
    isCreditMemo,
    appendix,
  };
}

function flattenLines(payload) {
  if (payload.linesByPlant && payload.linesByPlant.length > 0) {
    return payload.linesByPlant.flatMap(([, lines]) => lines);
  }
  return payload.lineItems || [];
}

function groupLinesByPlant(lineItems) {
  const map = new Map();
  for (const li of lineItems || []) {
    const key = li.plantCode || '—';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(li);
  }
  return Array.from(map.entries()).sort((a, b) => {
    if (a[0] === '—') return 1;
    if (b[0] === '—') return -1;
    return String(a[0]).localeCompare(String(b[0]));
  });
}

function estimateGroupHeight(doc, plantLines) {
  let h = 22; // banner
  for (const li of consolidatePlantLines(plantLines)) {
    h += BODY_LINE_H;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(BODY_FONT);
    const descLines = doc.splitTextToSize(String(li.description || ''), DESC_MAX_W);
    h += (descLines.length - 1) * 11;
    if (li.descSuffix) h += 10; // worst case: suffix wraps to its own line
    const dayDetails = Array.isArray(li.dayDetails) ? li.dayDetails : [];
    const isOT = li.coverageType === 'Overage';
    for (const d of dayDetails) {
      h += dayDetailLineCount(doc, d, isOT) * DAY_SUB_LINE_H;
    }
    const noteText = String(li.details || '').trim();
    if (noteText && (dayDetails.length === 0 || li.detailsEdited)) {
      const n = doc.splitTextToSize(noteText, EXPL_MAX_W).length;
      h += n * 10;
    }
  }
  return h + 8;
}

// Distinct liaison names for a plant group, comma-joined.
function distinctLiaisons(plantLines) {
  const names = [];
  for (const li of plantLines || []) {
    const n = String(li.submitterName || '').trim();
    if (n && !names.includes(n)) names.push(n);
  }
  return names.join(', ');
}

// Coverage label ("2 hrs / day", "5 hrs / week") from the supplier's
// per-plant assignment. Empty string when there's no coverage on file.
function coverageLabelFor(customer, plantCode) {
  const pas = customer?.plantAssignments || [];
  const code = String(plantCode || '').toUpperCase();
  const pa = pas.find((p) => String(p.plantCode || '').toUpperCase() === code);
  if (!pa) return '';
  const h = Number(pa.hoursAllocated);
  if (!Number.isFinite(h) || h <= 0) return '';
  const hrs = Number.isInteger(h) ? String(h) : h.toFixed(1);
  const unit = String(pa.hoursUnit || '').toLowerCase();
  let per = '';
  if (unit.includes('day')) per = 'day';
  else if (unit.includes('week')) per = 'week';
  else if (unit.includes('shift')) per = 'shift';
  else if (unit.includes('month')) per = 'month';
  else per = unit.replace('per', '').trim();
  return per ? `${hrs} hrs / ${per}` : `${hrs} hrs`;
}

function formatDateRangeShort(startIso, endIso) {
  if (!startIso) return '—';
  const m1 = String(startIso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m1) return '—';
  const mon = MONTHS[Number(m1[2]) - 1] || m1[2];
  if (!endIso || endIso === startIso) return `${mon} ${Number(m1[3])}`;
  const m2 = String(endIso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m2) return `${mon} ${Number(m1[3])}`;
  const mon2 = MONTHS[Number(m2[2]) - 1] || m2[2];
  if (m1[2] === m2[2]) return `${mon} ${Number(m1[3])} – ${Number(m2[3])}`;
  return `${mon} ${Number(m1[3])} – ${mon2} ${Number(m2[3])}`;
}

function formatWeekRangeShort(weekStartIso) {
  if (!weekStartIso) return '—';
  const m = String(weekStartIso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return weekStartIso;
  const start = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const startIso = `${start.getFullYear()}-${pad2(start.getMonth() + 1)}-${pad2(start.getDate())}`;
  const endIso = `${end.getFullYear()}-${pad2(end.getMonth() + 1)}-${pad2(end.getDate())}`;
  return formatDateRangeShort(startIso, endIso);
}

// Short M/D labels for the 7 days of a week grid, from the Sunday start.
function weekDatesShort(weekStartIso) {
  const m = String(weekStartIso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return [];
  const start = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const out = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    out.push(`${d.getMonth() + 1}/${d.getDate()}`);
  }
  return out;
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatQuantity(n) {
  const num = Number(n) || 0;
  return Number.isInteger(num) ? String(num) : num.toFixed(1);
}

// "+3" / "-2" / "0" for the Overages / Reductions rows. ASCII hyphen on
// purpose: the PDF's built-in Helvetica has no typographic minus glyph.
function formatSignedQuantity(n) {
  const num = Number(n) || 0;
  if (num === 0) return '0';
  return `${num > 0 ? '+' : '-'}${formatQuantity(Math.abs(num))}`;
}

function formatSignedCurrency(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const abs = Math.abs(num);
  const formatted = `$${abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  // ASCII hyphen-minus — the U+2212 minus glyph isn't in the PDF's
  // WinAnsi font and renders as a stray quote mark.
  return num < 0 ? `-${formatted}` : formatted;
}

// Plain positive currency ("$37.00", "$0.70") for the Rate column.
function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `$${num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDateForMeta(iso) {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso);
  const mon = MONTHS[Number(m[2]) - 1] || m[2];
  return `${mon} ${Number(m[3])}, ${m[1]}`;
}

function formatServiceDateRange(startIso, endIso) {
  const parse = (iso) => {
    const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? { y: m[1], mo: Number(m[2]), d: Number(m[3]) } : null;
  };
  const s = parse(startIso);
  const e = parse(endIso);
  const M = (n) => MONTHS[n - 1] || n;
  if (!s && !e) return '';
  if (s && !e) return `${M(s.mo)} ${s.d}, ${s.y}`;
  if (!s && e) return `${M(e.mo)} ${e.d}, ${e.y}`;
  if (s.y === e.y && s.mo === e.mo) return `${M(s.mo)} ${s.d} – ${e.d}, ${e.y}`;
  if (s.y === e.y) return `${M(s.mo)} ${s.d} – ${M(e.mo)} ${e.d}, ${e.y}`;
  return `${M(s.mo)} ${s.d}, ${s.y} – ${M(e.mo)} ${e.d}, ${e.y}`;
}

// The Attn line shows names only. Contacts are stored as names, but if
// a title slipped in ("Name — Title" or "Name, Title"), keep just the
// name.
function stripContactTitle(s) {
  const str = String(s || '').trim();
  const dash = str.indexOf(' — ');
  if (dash >= 0) return str.slice(0, dash).trim();
  const comma = str.indexOf(', ');
  if (comma >= 0) return str.slice(0, comma).trim();
  return str;
}

function composeAddressLines(invoice, customer) {
  const raw = invoice.BillingAddress || formatCustomerAddress(customer);
  if (!raw) return [];
  return String(raw)
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatCustomerAddress(customer) {
  if (!customer) return '';
  const cityLine = [customer.City, customer.State, customer.PostalCode]
    .filter(Boolean)
    .join(', ');
  const parts = [customer.AddressStreet, cityLine, formatCountry(customer.Country)].filter(
    Boolean,
  );
  return parts.join('\n');
}
